import { useState, useEffect, useRef, useMemo } from 'react';
import { loadShakaPlayer, getHistoryMeta } from '../utils/audioUtils';

const useShakaPlayer = ({
  currentItem,
  isRadioMode,
  radioState,
  isCountdown,
  radioMuted,
  setRadioMuted,
  onNext,
  shouldAutoPlay,
  history,
  partySession,
  videoElement,
}) => {
  const [playerInstance, setPlayerInstance] = useState(null);
  const [shakaLibLoaded, setShakaLibLoaded] = useState(false);
  const [playerError, setPlayerError] = useState(null);
  const [isSubtitlesOn, setIsSubtitlesOn] = useState(false);

  const loadedSrcRef = useRef(null);
  const lastSoughtIdRef = useRef(null);
  const loadIdRef = useRef(0);
  const isLoadingRef = useRef(false);

  // Use Ref for history to prevent re-loading source when progress is saved
  const historyRef = useRef(history);
  useEffect(() => {
    historyRef.current = history;
  }, [history]);

  // Initialize Shaka Library
  useEffect(() => {
    loadShakaPlayer()
      .then((shaka) => {
        shaka.polyfill.installAll();
        setShakaLibLoaded(true);
      })
      .catch((err) => {
        console.error('Failed to load Shaka library', err);
        setPlayerError('Failed to load media engine.');
      });
  }, []);

  // Create/Destroy Shaka Player Instance
  useEffect(() => {
    if (!shakaLibLoaded || !videoElement || playerInstance) {
      return;
    }

    console.log('Creating Shaka Player instance');
    const player = new window.shaka.Player(videoElement);

    player.addEventListener('error', (event) => {
      console.error('Shaka Error Code:', event.detail.code, event.detail);
      if (event.detail.severity === 2) {
        setPlayerError(`Playback Error: Code ${event.detail.code}`);
      }
    });

    player.configure({
      textDisplayFactory: () =>
        new window.shaka.text.SimpleTextDisplayer(videoElement),
      abr: { enabled: true },
      streaming: {
        bufferingGoal: 10,
        rebufferingGoal: 2,
        bufferBehind: 10,
      },
    });

    setPlayerInstance(player);

    return () => {
      if (player) {
        console.log('Cleaning up Shaka Player instance');
        player.destroy().catch((e) => console.warn('Player destroy error', e));
      }
      setPlayerInstance(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shakaLibLoaded, !!videoElement]); // Use boolean flag to stabilize

  // Unified Loading and Playback Effect
  useEffect(() => {
    if (!shakaLibLoaded || !videoElement || !playerInstance) {
      return;
    }

    // 1. Resolve Source URL
    const dashSrc =
      currentItem.stream || currentItem.audio || currentItem.preloadSrc;
    let hlsSrc = currentItem.hls;
    if (
      !hlsSrc &&
      dashSrc &&
      typeof dashSrc === 'string' &&
      dashSrc.includes('/stream/') &&
      dashSrc.includes('stream.mpd')
    ) {
      hlsSrc = dashSrc
        .replace('/stream/', '/hlsstream/')
        .replace('stream.mpd', 'master.m3u8');
    }

    const isShakaSupported =
      typeof window.shaka !== 'undefined' &&
      window.shaka.Player.isBrowserSupported();
    const canPlayNativeHLS =
      videoElement.canPlayType('application/vnd.apple.mpegurl') ||
      videoElement.canPlayType('application/x-mpegURL');
    const supportsMSE = !!window.MediaSource;

    let activeSrc = null;
    let mode = 'shaka';

    if (isShakaSupported && supportsMSE && dashSrc) {
      activeSrc = dashSrc;
      mode = 'shaka';
    } else if (canPlayNativeHLS && hlsSrc) {
      activeSrc = hlsSrc;
      mode = 'native';
    } else if (isShakaSupported && hlsSrc) {
      activeSrc = hlsSrc;
      mode = 'shaka';
    } else if (canPlayNativeHLS && dashSrc && !dashSrc.includes('.mpd')) {
      activeSrc = dashSrc;
      mode = 'native';
    }

    if (!activeSrc) {
      if (currentItem.id !== 'loading' && currentItem.id !== undefined) {
        setPlayerError('No compatible stream found for this device.');
      }
      return;
    }

    const currentLoadId = ++loadIdRef.current;
    const isNewSrc = loadedSrcRef.current !== activeSrc;

    // 2. Local Helper Functions
    const calculateStartTime = () => {
      if (isRadioMode && radioState) {
        const nowMs = Date.now();
        const offsetInBlockSeconds = (nowMs - radioState.blockStartMs) / 1000;
        if (typeof currentItem.playStartOffset === 'number') {
          let timeIntoBlock =
            offsetInBlockSeconds - currentItem.playStartOffset;
          if (currentItem.isLoopingFiller && currentItem.fillerDuration > 0) {
            const timeInLoopCycle = timeIntoBlock % currentItem.fillerDuration;
            return (
              (timeInLoopCycle + currentItem.fillerStartTime) %
              currentItem.fillerDuration
            );
          }
          return Math.max(0, timeIntoBlock);
        }
      }
      const savedMeta = getHistoryMeta(historyRef.current, currentItem.id);
      return currentItem.ignoreHistory ? 0 : savedMeta.progress || 0;
    };

    const syncPlayback = () => {
      if (currentLoadId !== loadIdRef.current || isLoadingRef.current) return;

      const video = videoElement;

      const isPartyWaiting =
        partySession &&
        partySession.itemId === currentItem.id &&
        Date.now() < partySession.startTime;
      if (isPartyWaiting) {
        if (!video.paused) video.pause();
        if (video.currentTime !== 0) video.currentTime = 0;
        return;
      }

      if (isRadioMode) {
        if (radioMuted) {
          if (!video.paused) video.pause();
        } else {
          const expectedTime = calculateStartTime();
          if (Math.abs(video.currentTime - expectedTime) > 2) {
            video.currentTime = expectedTime;
          }
          if (video.paused && !video.ended) {
            video.play().catch((e) => {
              if (e.name !== 'AbortError' && setRadioMuted) {
                setRadioMuted(true);
              }
            });
          }
        }
      } else {
        if (shouldAutoPlay && video.paused && !video.ended) {
          video.play().catch((e) => {
            if (e.name !== 'AbortError') console.warn('Autoplay blocked', e);
          });
        }
      }
    };

    const performSeek = (targetTime) => {
      if (videoElement && Math.abs(videoElement.currentTime - targetTime) > 2) {
        videoElement.currentTime = targetTime;
      }
      lastSoughtIdRef.current = currentItem.id;
    };

    // 3. Execution Logic
    if (isCountdown) {
      syncPlayback();
      return;
    }

    if (!isNewSrc) {
      if (lastSoughtIdRef.current !== currentItem.id) {
        const startTime = calculateStartTime();
        if (startTime > 0) {
          performSeek(startTime);
        } else {
          lastSoughtIdRef.current = currentItem.id;
        }
      }
      syncPlayback();
      return;
    }

    // New Source Loading
    console.log('Loading new source:', activeSrc, 'mode:', mode);
    setPlayerError(null);
    setIsSubtitlesOn(false);

    loadedSrcRef.current = activeSrc;
    isLoadingRef.current = true;

    const initialSeekTime = calculateStartTime();

    if (mode === 'shaka') {
      playerInstance
        .load(activeSrc, initialSeekTime)
        .then(async () => {
          if (currentLoadId !== loadIdRef.current) return;

          const subSrc = currentItem.subtitles || currentItem.preloadSubtitles;
          if (subSrc) {
            try {
              await playerInstance.addTextTrackAsync(
                subSrc,
                'hi',
                'subtitles',
                'text/srt'
              );
              playerInstance.setTextTrackVisibility(isSubtitlesOn);
            } catch (err) {
              /* silent */
            }
          }

          lastSoughtIdRef.current = currentItem.id;
          isLoadingRef.current = false;
          syncPlayback();
        })
        .catch((e) => {
          if (currentLoadId !== loadIdRef.current) return;
          if (e.code !== 7000) {
            // IGNORE_ABORTED
            isLoadingRef.current = false;
            setPlayerError(`Load Error: ${e.code}`);
          }
        });
    } else {
      // Native mode (Safari/HLS)
      playerInstance.unload().then(() => {
        if (currentLoadId !== loadIdRef.current) return;

        const onLoadedMetaData = () => {
          if (currentLoadId !== loadIdRef.current) return;
          if (initialSeekTime > 0) videoElement.currentTime = initialSeekTime;
          lastSoughtIdRef.current = currentItem.id;
          isLoadingRef.current = false;
          syncPlayback();
          videoElement.removeEventListener('loadedmetadata', onLoadedMetaData);
        };

        videoElement.addEventListener('loadedmetadata', onLoadedMetaData);
        videoElement.src = activeSrc;
        videoElement.load();
      });
    }
  }, [
    shakaLibLoaded,
    playerInstance,
    videoElement,
    currentItem.id,
    currentItem.stream,
    currentItem.audio,
    currentItem.preloadSrc,
    currentItem.hls,
    isRadioMode,
    radioState,
    isCountdown,
    partySession,
    shouldAutoPlay,
    radioMuted,
    isSubtitlesOn,
    setRadioMuted,
  ]);

  return useMemo(
    () => ({
      playerInstance,
      playerError,
      isSubtitlesOn,
      setIsSubtitlesOn,
    }),
    [playerInstance, playerError, isSubtitlesOn, setIsSubtitlesOn]
  );
};

export default useShakaPlayer;
